import { useAutomationActivation } from '@/hooks/api/automations/useAutomationPublications.ts';
import { Accordion, Alert, Box, Flex, Group, Menu, Stack, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Fragment, ReactNode, useCallback, useState } from 'react';
import { useFocusWithin, useHover, useMediaQuery } from '@mantine/hooks';
import { formatCalendarStyle, formatDuration } from '@/utils/datetime.ts';
import StatusIndicator from '@/components/StatusIndicator.tsx';
import RunMenu from '@/components/RunMenu.tsx';
import DatasetPill from '@/pages/Run/components/DatasetPill.tsx';
import { TriggerStep } from '@/types/automationSteps.ts';
import { ActiveStatuses, ExecutionStatus } from '@/types/statuses.ts';
import { AutomationVersion } from '@/types/automations.ts';
import {
  getRunAutomationVersionLabel,
  getRunCancellationSummary,
  getRunDisplayName,
  getRunVersionLabel,
  isBuilderTestRun,
  ReplayRunFromSourceSelection,
  Run,
  RunPreprocessingBlocker,
  RunWithDatasets,
} from '@/types/runs.ts';
import { TriggerDataset } from '@/types/datasets.ts';
import useRetrieveRunAutomationVersion from '@/hooks/api/runs/useRetrieveRunAutomationVersion.ts';
import RunMetadataModal from '@/pages/Run/components/RunMetadataModal.tsx';
import { HiOutlineCodeBracketSquare } from 'react-icons/hi2';
import { HiDotsVertical } from 'react-icons/hi';
import RunTypeBadge from '@/components/RunTypeBadge.tsx';
import useRetrieveRunTestOutcome from '@/hooks/api/runs/useRetrieveRunTestOutcome.ts';
import RunTestOutcomePanel from '@/pages/Run/components/RunTestOutcomePanel.tsx';
import DiagnosticDefinitionPanel from '@/pages/Run/components/DiagnosticDefinitionPanel.tsx';
import { getReplayRunFromSourceErrorMessage } from '@/hooks/api/runs/rerun/replayRunFromSourceErrors.ts';
import useReplayRunFromSource from '@/hooks/api/runs/rerun/useReplayRunFromSource.ts';
import HumanDecisionPanel from '@/pages/Run/components/HumanDecisionPanel.tsx';

type props = {
  runWithDatasets: RunWithDatasets;
};

const getTriggerName = (
  automationVersion: AutomationVersion | undefined,
  triggerDataset: TriggerDataset,
): string => {
  if (triggerDataset.port_name) {
    return `${triggerDataset.port_name}:`;
  }
  if (!triggerDataset.trigger_id || !automationVersion) {
    return 'Input:';
  }
  const step = automationVersion.steps.find(
    (step) => step.step_id === triggerDataset.trigger_step_id,
  );
  const triggerStep = step ? (step as TriggerStep) : undefined;
  const triggerInput = triggerStep?.manual_trigger_inputs.find(
    (input) => input.output_id === triggerDataset.trigger_id,
  );
  return triggerInput ? `${triggerInput.name}:` : 'Input:';
};

const hasRuntime = (run: Run): boolean =>
  run.total_runtime !== null && run.total_runtime !== undefined;

const makeRunTimingSentence = (run: Run): ReactNode => {
  const formattedStartedAt = run.last_run_at ? formatCalendarStyle(run.last_run_at) : null;
  const startedAt = formattedStartedAt === '-' ? null : formattedStartedAt;

  if (startedAt && hasRuntime(run)) {
    return (
      <>
        It started running {startedAt} and ran for {formatDuration(run.total_runtime)}
      </>
    );
  }
  if (startedAt) {
    return <>It started running {startedAt}</>;
  }
  if (hasRuntime(run)) {
    return <>It ran for {formatDuration(run.total_runtime)}</>;
  }
  return <>Execution timing is unavailable</>;
};

const makeTerminalRunSummary = (
  run: Run,
  statusText: string,
  beforeExecutionStartedText?: string,
): ReactNode => {
  const runDisplayName = getRunDisplayName(run);

  if (!run.latest_execution && beforeExecutionStartedText) {
    return (
      <>
        {runDisplayName} {beforeExecutionStartedText}
      </>
    );
  }

  return (
    <>
      {runDisplayName} {statusText}. {makeRunTimingSentence(run)}
    </>
  );
};

const makeCancellationProvenance = (run: Run): ReactNode => {
  if (!run.cancellation) {
    return null;
  }
  const cancellationTime = formatCalendarStyle(run.cancellation.cancelled_at);
  const summary = getRunCancellationSummary(run.cancellation);
  const punctuatedSummary = /[.!?]$/.test(summary) ? summary : `${summary}.`;
  return (
    <>
      {' '}
      {punctuatedSummary}
      {cancellationTime !== '-' && <> Cancellation time: {cancellationTime}.</>}
    </>
  );
};

const makeRunSummary = (run?: Run): ReactNode => {
  if (!run) {
    return <></>;
  }

  const runDisplayName = getRunDisplayName(run);

  if (run.preprocessing_error && run.status !== ExecutionStatus.Cancelled) {
    return (
      <>
        {runDisplayName} ran into an error before execution could start: {run.preprocessing_error}.
      </>
    );
  } else if ([ExecutionStatus.Pending, ExecutionStatus.Preprocessing].includes(run.status)) {
    return (
      <>
        {runDisplayName} is preprocessing input data sources
        {/*<Loader style={{ '--loader-size': '14px', animationDuration: '1.0s' }} color={'#51ADFF'} />*/}
      </>
    );
  } else if (run.status === ExecutionStatus.Active) {
    return (
      <>
        {runDisplayName} is running
        {/*<Loader style={{ '--loader-size': '14px', animationDuration: '1.0s' }} color={'#51ADFF'} />*/}
      </>
    );
  } else if (run.status === ExecutionStatus.RerunPreparing) {
    return <>{runDisplayName} is preparing to rerun</>;
  } else if (run.status === ExecutionStatus.Rerunning) {
    return <>{runDisplayName} is rerunning</>;
  } else if (run.status === ExecutionStatus.AskUser) {
    return (
      <>
        {runDisplayName} is paused and needs input before it can continue.{' '}
        {makeRunTimingSentence(run)}
      </>
    );
  } else if (run.status === ExecutionStatus.Errors) {
    return (
      <>{makeTerminalRunSummary(run, 'failed with errors', 'failed before execution started.')}</>
    );
  } else if (run.status === ExecutionStatus.Cancelled) {
    return (
      <>
        {makeTerminalRunSummary(run, 'was cancelled', 'was cancelled before execution started.')}
        {makeCancellationProvenance(run)}
      </>
    );
  } else if (run.status === ExecutionStatus.Completed) {
    return <>{makeTerminalRunSummary(run, 'completed successfully')}</>;
  }
};

const makeRunLivenessSummary = (run: Run): string | null => {
  const liveness = run.liveness;
  if (!liveness || !ActiveStatuses.includes(run.status)) {
    return null;
  }

  if (liveness.state === 'stale_recovery_eligible') {
    return 'No recent worker activity is recorded. Automatic recovery is eligible.';
  }
  if (liveness.state === 'unavailable') {
    return 'Current activity cannot be confirmed from durable runtime records.';
  }
  return liveness.summary;
};

const formatBlockerLabel = (blocker: RunPreprocessingBlocker): string => {
  const sourceLabel =
    blocker.source_type === 'static'
      ? 'Static dataset'
      : blocker.source_type === 'linked'
        ? 'Linked dataset'
        : blocker.source_type === 'trigger'
          ? 'Input dataset'
          : 'Dataset';
  const datasetLabel = blocker.dataset_name || blocker.dataset_id;
  const reason = blocker.processing_error || blocker.reason;
  return `${sourceLabel}: ${datasetLabel} (${reason})`;
};

const RUN_SUMMARY_HEADER_PADDING_X = 16;
const RUN_SUMMARY_HEADER_PADDING_Y = 2;
const RUN_SUMMARY_MENU_RIGHT_OFFSET = 36;
const RUN_SUMMARY_MENU_TRIGGER_SIZE = 22;
const RUN_SUMMARY_HEADER_MENU_CLEARANCE = 28;

const formatOutcomeAssessmentState = (assessment: NonNullable<Run['business_outcome_assessment']>): string => {
  if (assessment.resolution_state === 'evaluation_unavailable') {
    return 'Evaluation unavailable';
  }
  if (assessment.resolution_state === 'evaluating') {
    return 'Evaluating';
  }
  if (assessment.resolution_state === 'awaiting_completion') {
    return 'Awaiting run completion';
  }
  return assessment.state?.replaceAll('_', ' ') ?? assessment.resolution_state.replaceAll('_', ' ');
};

const RunSummaryCard = ({ runWithDatasets }: props) => {
  const run = runWithDatasets.run;
  const humanDecisionId = new URLSearchParams(window.location.search).get('humanDecisionId');

  const { data: automationVersion } = useRetrieveRunAutomationVersion(run.id);
  const activation = useAutomationActivation(run.automation_id);
  const { refetch: refreshActivation } = activation;
  const { mutate: replayRunFromSource, isPending: isReplayingFromSource } =
    useReplayRunFromSource();

  // const runHasPinnedSteps = !!automationVersion && automationVersion.pinned_steps.length > 0;

  // const { data: pinnedResults } = useRetrievePinnedResults();

  // const { mutate: updateRun, isPending: isUpdatingRun } = useUpdateRun(run.id);

  const [value, setValue] = useState<string | null>(humanDecisionId ? 'run-summary' : null);
  const [showMetadata, setShowMetadata] = useState(false);
  const { hovered, ref: hoverRef } = useHover();
  const { focused: isFocusWithin, ref: focusWithinRef } = useFocusWithin();
  const isTouchDevice = useMediaQuery('(hover: none)', false, {
    getInitialValueInEffect: false,
  });
  const runSummaryRef = useCallback(
    (node: HTMLDivElement | null) => {
      hoverRef(node);
      focusWithinRef(node);
    },
    [focusWithinRef, hoverRef],
  );
  const preprocessingBlockers = run.preprocessing_blockers ?? [];
  const staticDatasets = runWithDatasets.static_datasets ?? [];
  const linkedDatasets = runWithDatasets.linked_datasets ?? [];
  const runDisplayName = getRunDisplayName(run);
  const automationVersionLabel = getRunAutomationVersionLabel(run);
  const runVersionBadgeLabel = getRunVersionLabel(run);
  const interventionResponse = run.latest_execution?.intervention_response;
  const outcomeAssessment = run.business_outcome_assessment;
  const isBuilderTest = isBuilderTestRun(run);
  const livenessSummary = makeRunLivenessSummary(run);
  const shouldLoadTestOutcome = value === 'run-summary' && isBuilderTest;
  const isExpanded = value === 'run-summary';
  const {
    data: testOutcome,
    isFetching: isFetchingTestOutcome,
    error: testOutcomeError,
  } = useRetrieveRunTestOutcome(run.id, shouldLoadTestOutcome);
  const canReplayFromSource =
    run.run_mode === 'operational' &&
    !ActiveStatuses.includes(run.status) &&
    Boolean(run.trigger_step_id) &&
    !run.preprocessing_error;
  const handleReplayFromSource = useCallback(
    (selection: ReplayRunFromSourceSelection) => {
      if (
        selection === 'active' &&
        (!activation.data || activation.isError || activation.isFetching)
      )
        return;
      replayRunFromSource(
        {
          runId: run.id,
          replayRequest:
            selection === 'active'
              ? { selection: 'active', expected_activation: activation.data! }
              : { selection: 'original' },
        },
        {
          onSuccess: (newRun) => {
            notifications.show({
              title: 'New run queued',
              message: `${getRunDisplayName(newRun)} is preparing to run.`,
              color: 'green',
            });
          },
          onError: (error) => {
            if (selection === 'active') void refreshActivation();
            notifications.show({
              title: 'Unable to replay run',
              message: getReplayRunFromSourceErrorMessage(error, selection),
              color: 'red',
            });
          },
        },
      );
    },
    [
      activation.data,
      activation.isError,
      activation.isFetching,
      refreshActivation,
      replayRunFromSource,
      run.id,
    ],
  );

  const replayMenuItem = canReplayFromSource ? (
    <>
      <Menu.Item
        disabled={isReplayingFromSource}
        onClick={() => handleReplayFromSource('original')}
      >
        Replay original composition — external actions permitted
      </Menu.Item>
      <Menu.Item
        disabled={
          isReplayingFromSource ||
          activation.isError ||
          activation.isFetching ||
          !activation.isFetchedAfterMount ||
          !activation.data?.published_version_id
        }
        onClick={() => handleReplayFromSource('active')}
      >
        Replay current published composition — external actions permitted
      </Menu.Item>
      {activation.isError && (
        <Menu.Item onClick={() => void activation.refetch()}>Refresh published selection</Menu.Item>
      )}
    </>
  ) : null;

  return (
    <Accordion
      ref={runSummaryRef}
      chevronIconSize={16}
      styles={{
        label: { paddingTop: 'xs', paddingBottom: 'xs' },
      }}
      value={value}
      onChange={setValue}
    >
      <Accordion.Item
        key={'run-summary'}
        value={'run-summary'}
        style={{
          borderRadius: '12px',
          border: '1px solid #ECECED',
        }}
      >
        <Box style={{ position: 'relative', backgroundColor: 'white', borderRadius: '12px' }}>
          <Accordion.Control
            style={{
              borderRadius: '12px',
              backgroundColor: 'white',
              minWidth: 0,
              padding: `${RUN_SUMMARY_HEADER_PADDING_Y}px ${RUN_SUMMARY_HEADER_PADDING_X}px`,
            }}
          >
            <Group
              wrap={'nowrap'}
              gap={'6px'}
              mr={'4px'}
              align="center"
              style={{ minWidth: 0, paddingRight: `${RUN_SUMMARY_HEADER_MENU_CLEARANCE}px` }}
            >
              <Flex align="center" style={{ flexShrink: 0 }}>
                <StatusIndicator status={run.status} include_text={false} />
              </Flex>
              <Stack gap={5} style={{ flex: 1, minWidth: 0 }}>
                <Tooltip
                  label={runDisplayName}
                  disabled={isExpanded}
                  openDelay={250}
                  position="top-start"
                  multiline
                  maw={480}
                >
                  <Text
                    size={'md'}
                    fw={500}
                    truncate={isExpanded ? undefined : 'end'}
                    lh={1.25}
                    style={{
                      whiteSpace: isExpanded ? 'normal' : undefined,
                      wordBreak: isExpanded ? 'break-word' : undefined,
                    }}
                  >
                    {runDisplayName}
                  </Text>
                </Tooltip>
                <Group gap="xs" wrap="wrap" style={{ minWidth: 0 }}>
                  <RunTypeBadge run={run} />
                  {automationVersionLabel && !runVersionBadgeLabel && (
                    <Text size="xs" c="dimmed">
                      Version {automationVersionLabel}
                    </Text>
                  )}
                </Group>
              </Stack>
            </Group>
          </Accordion.Control>
          <Box
            style={{
              position: 'absolute',
              right: `${RUN_SUMMARY_MENU_RIGHT_OFFSET}px`,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              zIndex: 1,
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <RunMenu
              run={run}
              visible={hovered || isFocusWithin || isTouchDevice}
              triggerIcon={<HiDotsVertical size={15} color="#98A2B3" />}
              triggerSize={RUN_SUMMARY_MENU_TRIGGER_SIZE}
              loading={isReplayingFromSource}
              extraMenuItems={
                <Menu.Item
                  onClick={() => setShowMetadata(true)}
                  leftSection={
                    <HiOutlineCodeBracketSquare size={14} style={{ marginRight: '-4px' }} />
                  }
                >
                  Metadata
                </Menu.Item>
              }
              postRerunMenuItems={replayMenuItem}
            />
          </Box>
          <RunMetadataModal
            runWithDatasets={runWithDatasets}
            opened={showMetadata}
            onClose={() => setShowMetadata(false)}
          />
        </Box>
        <Accordion.Panel>
          <Stack gap={'md'}>
            <Group wrap={'nowrap'} gap={'xs'}>
              {makeRunSummary(run)}
            </Group>
            {livenessSummary && (
              <Text size="sm" c="dimmed">
                {livenessSummary}
              </Text>
            )}
            {outcomeAssessment && outcomeAssessment.resolution_state !== 'not_configured' && (
              <Alert
                color={
                  outcomeAssessment.state === 'achieved'
                    ? 'green'
                    : outcomeAssessment.state === 'not_achieved'
                      ? 'red'
                      : 'yellow'
                }
                variant="light"
                title={`Outcome assessment${outcomeAssessment.label ? `: ${outcomeAssessment.label}` : ''}`}
              >
                <Text size="sm">
                  {formatOutcomeAssessmentState(outcomeAssessment)}
                  {outcomeAssessment.summary
                    ? ` — ${outcomeAssessment.summary}`
                    : ''}
                </Text>
              </Alert>
            )}
            {interventionResponse && (
              <Alert color="green" variant="light" title="Structured review recorded">
                <Stack gap={4}>
                  <Text size="sm">
                    {interventionResponse.created_by_label} answered the review for `
                    {interventionResponse.target_step_path}`.
                  </Text>
                  <Text size="sm">
                    The response is immutable and is attached only to this continuation execution.
                  </Text>
                  <pre style={{ margin: 0, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(interventionResponse.values, null, 2)}
                  </pre>
                </Stack>
              </Alert>
            )}
            {humanDecisionId && <HumanDecisionPanel runId={run.id} decisionId={humanDecisionId} />}
            {isBuilderTest && (
              <RunTestOutcomePanel
                outcome={testOutcome}
                isLoading={isFetchingTestOutcome}
                errorMessage={testOutcomeError?.message ?? null}
              />
            )}
            {(run.test_purpose === 'diagnostic' || testOutcome?.test_purpose === 'diagnostic') && (
              <DiagnosticDefinitionPanel automationId={run.automation_id} runId={run.id} />
            )}
            {preprocessingBlockers.length > 0 && (
              <Alert color="yellow" variant="light" title="Preprocessing blockers">
                <Stack gap={4}>
                  {preprocessingBlockers.map((blocker) => (
                    <Text
                      key={`${blocker.source_type}-${blocker.dataset_id}-${blocker.static_dataset_id || blocker.linked_dataset_id || blocker.trigger_id || ''}`}
                      size="sm"
                    >
                      {formatBlockerLabel(blocker)}
                    </Text>
                  ))}
                </Stack>
              </Alert>
            )}
            {runWithDatasets.input_datasets.map((triggerDataset) => (
              <Fragment key={triggerDataset.id}>
                <Stack gap="8px">
                  <Text fw={500} w={'100%'} c={'757575'} size={'12px'}>
                    {getTriggerName(automationVersion, triggerDataset)}
                  </Text>
                  <DatasetPill
                    datasetId={triggerDataset.dataset.id}
                    datasetName={triggerDataset.dataset.name}
                  />
                </Stack>
              </Fragment>
            ))}
            {staticDatasets.length > 0 && (
              <Stack gap="8px">
                <Text fw={500} w={'100%'} c={'757575'} size={'12px'}>
                  Static datasets:
                </Text>
                <Group gap={'xs'}>
                  {staticDatasets.map((staticDataset) => (
                    <DatasetPill
                      key={staticDataset.static_dataset_id}
                      datasetId={staticDataset.dataset.id}
                      datasetName={staticDataset.dataset.name}
                    />
                  ))}
                </Group>
              </Stack>
            )}
            {linkedDatasets.length > 0 && (
              <Stack gap="8px">
                <Text fw={500} w={'100%'} c={'757575'} size={'12px'}>
                  Linked datasets:
                </Text>
                <Group gap={'xs'}>
                  {linkedDatasets.map((linkedDataset) => (
                    <DatasetPill
                      key={linkedDataset.linked_dataset_id}
                      datasetId={linkedDataset.dataset.id}
                      datasetName={linkedDataset.dataset.name}
                    />
                  ))}
                </Group>
              </Stack>
            )}
            {runWithDatasets.output_datasets.length > 0 && (
              <Stack gap="xs">
                <Text fw={500} w={'100%'} c={'757575'} size={'12px'}>
                  Output:
                </Text>

                <Group gap={'xs'}>
                  {runWithDatasets.output_datasets.map((result) => (
                    <DatasetPill
                      key={result.step_id}
                      datasetId={result.dataset.id}
                      datasetName={result.dataset.name}
                    />
                  ))}
                </Group>
              </Stack>
            )}
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export default RunSummaryCard;
